import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './Header.css';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { BookingValidatedNotificationItem, ChatThreadItem, clearBookingNotifications, logout, markChatThreadAsReadLocal } from '../../features/auth/authSlice';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchCurrentUser, login, logoutCurrentUser, markAllNotificationsAsRead } from '../../features/auth/authThunks';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { googleSigninApi, resendSignupConfirmationApi } from '../../api/authApi';
import {
  deleteCurrentUserMessageApi,
  listCurrentUserMessagesByInterlocutorApi,
  markCurrentUserConversationAsReadApi,
  sendCurrentUserMessageApi,
  sendCurrentUserTypingSignalApi,
} from '../../api/chatApi';
import { getBookingsByOwnerAdsApi, getBookingsByUserApi, getPaginatedAdsApi } from '../../api/adsApi';
import ChatMessageContent from '../ChatMessageContent/ChatMessageContent';
import { FiBell, FiBookmark, FiClipboard, FiEye, FiEyeOff, FiGlobe, FiGrid, FiHome, FiLogOut, FiMail, FiMenu, FiPlusCircle, FiUser, FiX } from 'react-icons/fi';

const BellIcon = FiBell as unknown as React.ComponentType<any>;
const BookmarkIcon = FiBookmark as unknown as React.ComponentType<any>;
const ClipboardIcon = FiClipboard as unknown as React.ComponentType<any>;
const EyeIcon = FiEye as unknown as React.ComponentType<any>;
const EyeOffIcon = FiEyeOff as unknown as React.ComponentType<any>;
const GlobeIcon = FiGlobe as unknown as React.ComponentType<any>;
const GridIcon = FiGrid as unknown as React.ComponentType<any>;
const HomeIcon = FiHome as unknown as React.ComponentType<any>;
const LogoutIcon = FiLogOut as unknown as React.ComponentType<any>;
const MailIcon = FiMail as unknown as React.ComponentType<any>;
const MenuIcon = FiMenu as unknown as React.ComponentType<any>;
const PlusCircleIcon = FiPlusCircle as unknown as React.ComponentType<any>;
const UserIcon = FiUser as unknown as React.ComponentType<any>;
const CloseIcon = FiX as unknown as React.ComponentType<any>;

type ChatWindowItem =
  | { type: 'date'; id: string; label: string }
  | { type: 'message'; id: string; message: { id: string; content: string; isMine: boolean; createdAt: string; readAt?: string } };

type ChatPaginationCursor = {
  before?: string;
  beforeId?: string;
} | null;

const createClientMessageId = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const CHAT_INPUT_MIN_HEIGHT = 30;
const CHAT_INPUT_MAX_HEIGHT = 78;
const CHAT_MESSAGES_PAGE_SIZE = 20;

const resizeChatInputElement = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = `${CHAT_INPUT_MIN_HEIGHT}px`;
  textarea.style.height = `${Math.min(textarea.scrollHeight, CHAT_INPUT_MAX_HEIGHT)}px`;
};

const MY_AVATAR_URI = `data:image/svg+xml;utf8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 88 88'><defs><linearGradient id='g1' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#2f80ed'/><stop offset='100%' stop-color='#1b3e7a'/></linearGradient></defs><rect width='88' height='88' rx='44' fill='url(#g1)'/><circle cx='44' cy='31' r='15' fill='#e8f2ff'/><path d='M18 74c0-14 12-22 26-22s26 8 26 22' fill='#cfe4ff'/><circle cx='33' cy='30' r='2.5' fill='#1f4f93'/><circle cx='55' cy='30' r='2.5' fill='#1f4f93'/></svg>"
)}`;

const OTHER_AVATAR_URI = `data:image/svg+xml;utf8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 88 88'><defs><linearGradient id='g2' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#16a085'/><stop offset='100%' stop-color='#0e5f53'/></linearGradient></defs><rect width='88' height='88' rx='44' fill='url(#g2)'/><circle cx='44' cy='31' r='15' fill='#e8fff8'/><path d='M18 74c0-14 12-22 26-22s26 8 26 22' fill='#c7f3e7'/><path d='M26 25c4-7 10-10 18-10s14 3 18 10' stroke='#0d5f53' stroke-width='4' fill='none' stroke-linecap='round'/></svg>"
)}`;

const capitalizeDisplayName = (value: string) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const languageItems = [
    { code: 'ar', label: 'العربية', flagSrc: 'https://flagcdn.com/w20/dz.png', flagAlt: 'Algeria' },
    { code: 'en', label: 'English', flagSrc: 'https://flagcdn.com/w20/us.png', flagAlt: 'United States' },
    { code: 'fr', label: 'Français', flagSrc: 'https://flagcdn.com/w20/fr.png', flagAlt: 'France' },
  ] as const;
  const currentLang = (i18n.resolvedLanguage || 'fr').split('-')[0];
  const navigate = useNavigate();
  const location = useLocation();
  const shouldShowDevBanner =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === 'dev.krydz.pro');
  const [menuOpen, setMenuOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const [accountOpen, setAccountOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [mobileLanguageOpen, setMobileLanguageOpen] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [bellAnimating, setBellAnimating] = useState(false);
  const [chatAnimating, setChatAnimating] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [chatWindowOpen, setChatWindowOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<BookingValidatedNotificationItem | null>(null);
  const [showReservationDetails, setShowReservationDetails] = useState(false);
  const [selectedChatThread, setSelectedChatThread] = useState<ChatThreadItem | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{ id: string; content: string; isMine: boolean; createdAt: string; readAt?: string }>>([]);
  const [chatDeleteCandidate, setChatDeleteCandidate] = useState<{ id: string; content: string } | null>(null);
  const [accountConfirmationOpen, setAccountConfirmationOpen] = useState(false);
  const [missingPhoneModalOpen, setMissingPhoneModalOpen] = useState(false);
  const [resendConfirmationEmail, setResendConfirmationEmail] = useState('');
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatLoadingOlder, setChatLoadingOlder] = useState(false);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatNextCursor, setChatNextCursor] = useState<ChatPaginationCursor>(null);
  const [chatSending, setChatSending] = useState(false);
  const [chatDeleting, setChatDeleting] = useState(false);
  const [canViewMyAds, setCanViewMyAds] = useState(false);
  const [canViewMyReservations, setCanViewMyReservations] = useState(false);
  const [canViewMyBookings, setCanViewMyBookings] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const loginDropdownRef = useRef<HTMLDivElement | null>(null);
  const languageDropdownRef = useRef<HTMLDivElement | null>(null);
  const accountDropdownRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const notificationsPanelRef = useRef<HTMLDivElement | null>(null);
  const chatsPanelRef = useRef<HTMLDivElement | null>(null);
  const desktopBellRef = useRef<HTMLButtonElement | null>(null);
  const desktopChatRef = useRef<HTMLButtonElement | null>(null);
  const mobileBellRef = useRef<HTMLButtonElement | null>(null);
  const mobileChatRef = useRef<HTMLButtonElement | null>(null);
  const chatMessagesRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const typingStopTimerRef = useRef<number | null>(null);
  const typingActiveRef = useRef(false);
  const chatSendingRef = useRef(false);
  const chatClientMessageIdRef = useRef<string | null>(null);
  const pendingAutoScrollRef = useRef(false);
  const chatNearBottomRef = useRef(true);
  const chatLoadingOlderRef = useRef(false);
  const preserveChatScrollRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const chatTouchStartYRef = useRef<number | null>(null);

  const { user, loading, initialized, notificationCount, notificationTick, notifications, chatUnreadCount, chatTick, chatThreads, chatReadByInterlocutor, chatTypingByInterlocutor, latestChatMessage, latestDeletedChatMessage } = useSelector((state: RootState) => state.auth);
  const authResolved = initialized;
  const isAuthenticated = Boolean(user);
  const [typingClock, setTypingClock] = useState(Date.now());
  const [notificationPanelStyle, setNotificationPanelStyle] = useState<React.CSSProperties>({});
  const [chatPanelStyle, setChatPanelStyle] = useState<React.CSSProperties>({});
  const userRole = String(user?.role || 'user').toLowerCase();
  const canAccessDashboard = ['admin','moderator'].includes(userRole);
  const userProfileEditPath = user?.id || user?._id ? `/profile/${encodeURIComponent(String(user.id || user._id))}` : '/profile-info';
  const userHasPhone = String(user?.phone || '').replace(/\D/g, '').length >= 6;
  const selectedInterlocutorId = String(selectedChatThread?.interlocutorId || '');
  const isSelectedInterlocutorTyping = Boolean(
    selectedInterlocutorId &&
    Number(chatTypingByInterlocutor[selectedInterlocutorId] || 0) > typingClock
  );
  const lastChatMessage = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1] : null;
  const isMessagesPage = location.pathname.startsWith('/messages');
  const locale = i18n.resolvedLanguage || 'fr';
  const myDisplayName = capitalizeDisplayName(String(
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email ||
    'Moi'
  ));
  const selectedInterlocutorName = capitalizeDisplayName(String(selectedChatThread?.interlocutorName || 'Utilisateur'));
  const myAvatar = MY_AVATAR_URI;
  const interlocutorAvatar = OTHER_AVATAR_URI;

  const handleAddAdLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, options: { mobile?: boolean } = {}) => {
    if (userHasPhone) {
      setAccountOpen(false);
      if (options.mobile) setMenuOpen(false);
      return;
    }

    event.preventDefault();
    setAccountOpen(false);
    if (options.mobile) setMenuOpen(false);
    setMissingPhoneModalOpen(true);
  }, [userHasPhone]);

  const formatChatMessageTime = useCallback((value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }, [locale]);

  const formatChatDateSeparator = useCallback((value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(parsed).toUpperCase();
  }, [locale]);

  const chatWindowItems = useMemo<ChatWindowItem[]>(() => {
    const items: ChatWindowItem[] = [];
    let previousDateKey = '';

    chatMessages.forEach((message) => {
      const parsed = new Date(message.createdAt);
      const dateKey = Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
      if (dateKey && dateKey !== previousDateKey) {
        items.push({
          type: 'date',
          id: `date-${dateKey}`,
          label: formatChatDateSeparator(message.createdAt),
        });
        previousDateKey = dateKey;
      }
      items.push({
        type: 'message',
        id: message.id,
        message,
      });
    });

    return items;
  }, [chatMessages, formatChatDateSeparator]);

  useEffect(() => {
    if (!isMessagesPage) return;
    setChatWindowOpen(false);
    setChatDeleteCandidate(null);
  }, [isMessagesPage]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('header-dev-banner-enabled', shouldShowDevBanner);
    return () => {
      document.documentElement.classList.remove('header-dev-banner-enabled');
    };
  }, [shouldShowDevBanner]);

  useEffect(() => {
    const body = document.body;
    const desktopMedia = window.matchMedia('(min-width: 1201px)');
    const syncChatLayoutClass = () => {
      if (chatWindowOpen && desktopMedia.matches) {
        body.classList.add('header-chat-window-open');
      } else {
        body.classList.remove('header-chat-window-open');
      }
    };

    syncChatLayoutClass();
    desktopMedia.addEventListener('change', syncChatLayoutClass);

    return () => {
      desktopMedia.removeEventListener('change', syncChatLayoutClass);
      body.classList.remove('header-chat-window-open');
    };
  }, [chatWindowOpen]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (loginDropdownRef.current && !loginDropdownRef.current.contains(target)) {
        setLoginOpen(false);
      }

      if (languageDropdownRef.current && !languageDropdownRef.current.contains(target)) {
        setLanguageOpen(false);
      }

      if (accountDropdownRef.current && !accountDropdownRef.current.contains(target)) {
        setAccountOpen(false);
      }

      const isInsideNotificationsPanel = Boolean(
        notificationsPanelRef.current && notificationsPanelRef.current.contains(target)
      );
      const isInsideChatsPanel = Boolean(
        chatsPanelRef.current && chatsPanelRef.current.contains(target)
      );
      const isInsideDesktopBell = Boolean(desktopBellRef.current && desktopBellRef.current.contains(target));
      const isInsideDesktopChat = Boolean(desktopChatRef.current && desktopChatRef.current.contains(target));
      const isInsideMobileBell = Boolean(mobileBellRef.current && mobileBellRef.current.contains(target));
      const isInsideMobileChat = Boolean(mobileChatRef.current && mobileChatRef.current.contains(target));
      if (!isInsideNotificationsPanel && !isInsideDesktopBell && !isInsideMobileBell) {
        setNotificationOpen(false);
      }
      if (!isInsideChatsPanel && !isInsideDesktopChat && !isInsideMobileChat) {
        setChatMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoginOpen(false);
  }, [user]);

  useEffect(() => {
    if (user) return;
    setNotificationOpen(false);
    setChatMenuOpen(false);
    setChatWindowOpen(false);
    setSelectedNotification(null);
    setSelectedChatThread(null);
    setChatMessages([]);
    setChatHasMore(false);
    setChatNextCursor(null);
    setChatInput('');
    setCanViewMyAds(false);
    setCanViewMyReservations(false);
    setCanViewMyBookings(false);
  }, [user]);

  useEffect(() => {
    const ownerId = String(user?.id || user?._id || '');
    if (!ownerId) {
      setCanViewMyAds(false);
      setCanViewMyReservations(false);
      setCanViewMyBookings(false);
      return;
    }

    let cancelled = false;
    const shouldEvaluateLinks = accountOpen || menuOpen;
    if (!shouldEvaluateLinks) return;

    const run = async () => {
      try {
        const [adsResponse, bookingsResponse, myBookingsResponse] = await Promise.all([
          getPaginatedAdsApi({ idOwner: ownerId, limit: 1 }),
          getBookingsByOwnerAdsApi(ownerId),
          getBookingsByUserApi(ownerId),
        ]);

        const ads = Array.isArray(adsResponse?.data?.data) ? adsResponse.data.data : [];
        const bookings = Array.isArray(bookingsResponse?.data?.data) ? bookingsResponse.data.data : [];
        const myBookings = Array.isArray(myBookingsResponse?.data?.data) ? myBookingsResponse.data.data : [];
        const hasAds = ads.length > 0;
        const hasReservations = bookings.some((booking: any) => {
          const bookingUserId = String(booking?.user?.id || booking?.user?._id || '');
          return Boolean(bookingUserId && bookingUserId !== ownerId);
        });
        const hasMyBookings = myBookings.some((booking: any) => {
          const bookingDates = Array.isArray(booking?.bookingDates) ? booking.bookingDates : [];
          return bookingDates.length > 0;
        });

        if (cancelled) return;
        setCanViewMyAds(hasAds);
        setCanViewMyReservations(hasReservations);
        setCanViewMyBookings(hasMyBookings);
      } catch {
        if (cancelled) return;
        setCanViewMyAds(false);
        setCanViewMyReservations(false);
        setCanViewMyBookings(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [accountOpen, menuOpen, user?.id, user?._id]);

  useEffect(() => {
    if (menuOpen) return;
    setMobileLanguageOpen(false);
  }, [menuOpen]);

  useEffect(() => {
    if (!user || notificationTick <= 0) return;
    setBellAnimating(true);
    const timer = window.setTimeout(() => setBellAnimating(false), 420);
    return () => window.clearTimeout(timer);
  }, [notificationTick, user]);

  useEffect(() => {
    if (!user || chatTick <= 0) return;
    setChatAnimating(true);
    const timer = window.setTimeout(() => setChatAnimating(false), 420);
    return () => window.clearTimeout(timer);
  }, [chatTick, user]);

  useEffect(() => {
    if (!chatWindowOpen || !selectedChatThread?.interlocutorId) return;
    let cancelled = false;
    setChatLoading(true);
    setChatHasMore(false);
    setChatNextCursor(null);
    preserveChatScrollRef.current = null;
    pendingAutoScrollRef.current = true;
    const run = async () => {
      try {
        const response = await listCurrentUserMessagesByInterlocutorApi(selectedChatThread.interlocutorId, {
          limit: CHAT_MESSAGES_PAGE_SIZE,
        });
        const incoming = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (cancelled) return;
        setChatMessages(
          incoming.map((item: any) => ({
            id: String(item?.id || `${item?.createdAt || Date.now()}`),
            content: typeof item?.content === 'string' ? item.content : '',
            isMine: Boolean(item?.isMine),
            createdAt: typeof item?.createdAt === 'string' ? item.createdAt : new Date().toISOString(),
            readAt: typeof item?.readAt === 'string' ? item.readAt : undefined,
          }))
        );
        setChatHasMore(Boolean(response?.data?.hasMore));
        setChatNextCursor(response?.data?.nextCursor || null);
      } catch (_error) {
        // noop
      } finally {
        if (cancelled) return;
        setChatLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [chatWindowOpen, selectedChatThread?.interlocutorId]);

  const scrollWindowChatToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    if (!chatMessagesRef.current) return;
    chatMessagesRef.current.scrollTo({
      top: chatMessagesRef.current.scrollHeight,
      behavior,
    });
  }, []);

  const forceWindowChatToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    scrollWindowChatToBottom(behavior);
    window.requestAnimationFrame(() => scrollWindowChatToBottom(behavior));
    window.setTimeout(() => scrollWindowChatToBottom(behavior), 80);
  }, [scrollWindowChatToBottom]);

  const loadOlderWindowChatMessages = useCallback(async () => {
    if (!selectedInterlocutorId || !chatHasMore || chatLoadingOlderRef.current || chatLoading || chatMessages.length === 0) return;
    const oldestMessage = chatMessages[0];
    const before = chatNextCursor?.before || oldestMessage?.createdAt;
    const beforeId = chatNextCursor?.beforeId || oldestMessage?.id;
    if (!before || !beforeId) return;
    const body = chatMessagesRef.current;
    preserveChatScrollRef.current = body
      ? { scrollHeight: body.scrollHeight, scrollTop: body.scrollTop }
      : null;
    chatLoadingOlderRef.current = true;
    setChatLoadingOlder(true);
    try {
      const response = await listCurrentUserMessagesByInterlocutorApi(selectedInterlocutorId, {
        limit: CHAT_MESSAGES_PAGE_SIZE,
        before,
        beforeId,
      });
      const incoming = Array.isArray(response?.data?.data) ? response.data.data : [];
      const mapped = incoming.map((item: any) => ({
        id: String(item?.id || `${item?.createdAt || Date.now()}`),
        content: typeof item?.content === 'string' ? item.content : '',
        isMine: Boolean(item?.isMine),
        createdAt: typeof item?.createdAt === 'string' ? item.createdAt : new Date().toISOString(),
        readAt: typeof item?.readAt === 'string' ? item.readAt : undefined,
      }));
      setChatHasMore(Boolean(response?.data?.hasMore));
      setChatNextCursor(response?.data?.nextCursor || null);
      setChatMessages((prev) => {
        const existingIds = new Set(prev.map((message) => message.id));
        const olderMessages = mapped.filter((message) => !existingIds.has(message.id));
        return [...olderMessages, ...prev].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      });
    } catch (_error) {
      preserveChatScrollRef.current = null;
    } finally {
      chatLoadingOlderRef.current = false;
      setChatLoadingOlder(false);
    }
  }, [chatHasMore, chatLoading, chatMessages, chatNextCursor?.before, chatNextCursor?.beforeId, selectedInterlocutorId]);

  useLayoutEffect(() => {
    const preserved = preserveChatScrollRef.current;
    const body = chatMessagesRef.current;
    if (!preserved || !body) return;
    body.scrollTop = body.scrollHeight - preserved.scrollHeight + preserved.scrollTop;
    preserveChatScrollRef.current = null;
  }, [chatMessages.length]);

  const handleWindowChatBodyScroll = useCallback(() => {
    const body = chatMessagesRef.current;
    if (!body) return;
    const distanceFromBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
    chatNearBottomRef.current = distanceFromBottom <= 40;
    if (body.scrollTop <= 48) {
      void loadOlderWindowChatMessages();
    }
  }, [loadOlderWindowChatMessages]);

  const handleWindowChatBodyWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (event.deltaY >= 0) return;
    const body = chatMessagesRef.current;
    if (!body || body.scrollTop > 80) return;
    void loadOlderWindowChatMessages();
  }, [loadOlderWindowChatMessages]);

  const handleWindowChatBodyTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    chatTouchStartYRef.current = event.touches[0]?.clientY ?? null;
  }, []);

  const handleWindowChatBodyTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const startY = chatTouchStartYRef.current;
    const currentY = event.touches[0]?.clientY;
    if (startY === null || currentY === undefined || currentY <= startY) return;
    const body = chatMessagesRef.current;
    if (!body || body.scrollTop > 80) return;
    void loadOlderWindowChatMessages();
  }, [loadOlderWindowChatMessages]);

  const resizeChatInput = useCallback(() => {
    const textarea = chatInputRef.current;
    if (!textarea) return;
    resizeChatInputElement(textarea);
  }, []);

  useEffect(() => {
    if (!chatMessages.length) return;
    if (preserveChatScrollRef.current) return;
    if (!pendingAutoScrollRef.current) return;
    forceWindowChatToBottom('smooth');
    pendingAutoScrollRef.current = false;
  }, [chatMessages, forceWindowChatToBottom]);

  useEffect(() => {
    setShowReservationDetails(false);
  }, [selectedNotification?.id]);

  useEffect(() => {
    const timer = window.setInterval(() => setTypingClock(Date.now()), 600);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (typingStopTimerRef.current) {
        window.clearTimeout(typingStopTimerRef.current);
        typingStopTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedInterlocutorId) return;
    const nextReadAt = chatReadByInterlocutor[selectedInterlocutorId];
    if (!nextReadAt) return;
    const nextReadTime = new Date(nextReadAt).getTime();
    setChatMessages((prev) => prev.map((message) => (
      message.isMine &&
      !message.readAt &&
      !Number.isNaN(nextReadTime) &&
      new Date(message.createdAt).getTime() <= nextReadTime
        ? { ...message, readAt: nextReadAt }
        : message
    )));
  }, [chatReadByInterlocutor, selectedInterlocutorId]);

  useEffect(() => {
    if (!latestChatMessage?.id) return;
    if (!chatWindowOpen || latestChatMessage.interlocutorId !== selectedInterlocutorId) return;
    const shouldAutoScroll = latestChatMessage.isMine || chatNearBottomRef.current;

    setChatMessages((prev) => {
      const existingIndex = prev.findIndex((message) => message.id === latestChatMessage.id);
      const nextMessage = {
        id: latestChatMessage.id,
        content: latestChatMessage.content,
        isMine: latestChatMessage.isMine,
        createdAt: latestChatMessage.createdAt,
        readAt: latestChatMessage.readAt,
      };

      if (existingIndex !== -1) {
        return prev.map((message, index) => (
          index === existingIndex ? { ...message, ...nextMessage } : message
        ));
      }

      return [...prev, nextMessage].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    });
    if (shouldAutoScroll) {
      pendingAutoScrollRef.current = true;
    }

    if (!latestChatMessage.isMine) {
      dispatch(markChatThreadAsReadLocal({ interlocutorId: latestChatMessage.interlocutorId }));
      markCurrentUserConversationAsReadApi(latestChatMessage.interlocutorId).catch(() => undefined);
    }
  }, [chatWindowOpen, dispatch, latestChatMessage, selectedInterlocutorId]);

  useEffect(() => {
    if (!latestDeletedChatMessage?.messageId) return;
    if (latestDeletedChatMessage.interlocutorId !== selectedInterlocutorId) return;
    setChatMessages((prev) => prev.filter((message) => message.id !== latestDeletedChatMessage.messageId));
    setChatDeleteCandidate((prev) => (
      prev?.id === latestDeletedChatMessage.messageId ? null : prev
    ));
  }, [latestDeletedChatMessage, selectedInterlocutorId]);

  const positionNotificationsPanel = useCallback(() => {
    const panel = notificationsPanelRef.current;
    const headerElement = headerRef.current;
    if (!panel || !headerElement) return;

    const desktopBell = desktopBellRef.current;
    const mobileBell = mobileBellRef.current;
    const useMobileBell = Boolean(mobileBell && window.getComputedStyle(mobileBell).display !== 'none');
    const bell = useMobileBell ? mobileBell : desktopBell;
    if (!bell) return;

    const viewportPadding = 12;
    const bellRect = bell.getBoundingClientRect();
    const headerRect = headerElement.getBoundingClientRect();
    const panelWidth = panel.getBoundingClientRect().width;
    const bellCenterX = bellRect.left + (bellRect.width / 2);

    let left = bellCenterX - panelWidth + 28;
    const minLeft = viewportPadding;
    const maxLeft = Math.max(minLeft, window.innerWidth - panelWidth - viewportPadding);
    left = Math.min(Math.max(left, minLeft), maxLeft);

    const arrowLeft = Math.min(Math.max(bellCenterX - left, 24), panelWidth - 24);
    const nextLeftInsideHeader = left - headerRect.left;

    setNotificationPanelStyle({
      left: `${nextLeftInsideHeader}px`,
      right: 'auto',
      ['--header-notifications-arrow-left' as any]: `${arrowLeft}px`,
    });
  }, []);

  const positionChatsPanel = useCallback(() => {
    const panel = chatsPanelRef.current;
    const headerElement = headerRef.current;
    if (!panel || !headerElement) return;

    const desktopChat = desktopChatRef.current;
    const mobileChat = mobileChatRef.current;
    const useMobileChat = Boolean(mobileChat && window.getComputedStyle(mobileChat).display !== 'none');
    const chatTrigger = useMobileChat ? mobileChat : desktopChat;
    if (!chatTrigger) return;

    const viewportPadding = 12;
    const triggerRect = chatTrigger.getBoundingClientRect();
    const headerRect = headerElement.getBoundingClientRect();
    const panelWidth = panel.getBoundingClientRect().width;
    const triggerCenterX = triggerRect.left + (triggerRect.width / 2);

    let left = triggerCenterX - panelWidth + 28;
    const minLeft = viewportPadding;
    const maxLeft = Math.max(minLeft, window.innerWidth - panelWidth - viewportPadding);
    left = Math.min(Math.max(left, minLeft), maxLeft);

    const arrowLeft = Math.min(Math.max(triggerCenterX - left, 24), panelWidth - 24);
    const nextLeftInsideHeader = left - headerRect.left;

    setChatPanelStyle({
      left: `${nextLeftInsideHeader}px`,
      right: 'auto',
      ['--header-chats-arrow-left' as any]: `${arrowLeft}px`,
    });
  }, []);

  useEffect(() => {
    if (!notificationOpen) return;
    positionNotificationsPanel();
    const handleResize = () => positionNotificationsPanel();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [notificationOpen, positionNotificationsPanel]);

  useEffect(() => {
    if (!chatMenuOpen) return;
    positionChatsPanel();
    const handleResize = () => positionChatsPanel();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [chatMenuOpen, positionChatsPanel]);

  const handleLogout = (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    void dispatch(logoutCurrentUser());
    dispatch(logout());
    setAccountOpen(false);
    setMenuOpen(false);
    navigate('/', { replace: true });
  };

  const handleLoginInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleResendConfirmationEmail = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = resendConfirmationEmail.trim();
    if (!email) {
      toast.error(t('header.confirmAccountEmailRequired'), {
        autoClose: 3000,
        theme: 'colored',
      });
      return;
    }

    setResendingConfirmation(true);
    try {
      const response = await resendSignupConfirmationApi({ email, lang: currentLang });
      const payloadMessage = response?.data?.message;
      const successMessage = typeof payloadMessage === 'string'
        ? payloadMessage
        : String(
            payloadMessage?.[currentLang] ||
            payloadMessage?.fr ||
            payloadMessage?.en ||
            payloadMessage?.ar ||
            t('header.confirmAccountResent')
          );
      toast.success(successMessage, {
        autoClose: 4000,
        theme: 'colored',
      });
      setAccountConfirmationOpen(false);
    } catch (error: any) {
      const payloadMessage = error?.response?.data?.message;
      const errorMessage = typeof payloadMessage === 'string'
        ? payloadMessage
        : String(
            payloadMessage?.[currentLang] ||
            payloadMessage?.fr ||
            payloadMessage?.en ||
            payloadMessage?.ar ||
            t('header.confirmAccountResendError')
          );
      toast.error(errorMessage, {
        autoClose: 4000,
        theme: 'colored',
      });
    } finally {
      setResendingConfirmation(false);
    }
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      await dispatch(
        login({
          email: credentials.email.trim(),
          password: credentials.password,
        })
      ).unwrap();
      toast.success(t('header.loginSuccess'), {
        autoClose: 3000,
        theme: 'colored',
      });
      window.setTimeout(() => {
        void dispatch(fetchCurrentUser());
      }, 150);
      setLoginOpen(false);
      setMenuOpen(false);
      setCredentials({ email: '', password: '' });
    } catch (_error: any) {
      setLoginOpen(false);
      if (_error?.code === 'ACCOUNT_NOT_CONFIRMED') {
        setResendConfirmationEmail(credentials.email.trim());
        setAccountConfirmationOpen(true);
        return;
      }
      const errorMessage = typeof _error === 'string'
        ? _error
        : (typeof _error === 'object' && _error !== null
          ? String(
              (_error as Record<string, unknown>)[currentLang] ||
              (_error as Record<string, unknown>).fr ||
              (_error as Record<string, unknown>).en ||
              (_error as Record<string, unknown>).ar ||
              t('header.loginError')
            )
          : t('header.loginError'));
      toast.error(errorMessage, {
        autoClose: 3000,
        theme: 'colored',
      });
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse: CredentialResponse) => {
    const credential = credentialResponse.credential;
    if (!credential) {
      toast.error(t('header.loginError'), {
        autoClose: 3000,
        theme: 'colored',
      });
      return;
    }

    try {
      await googleSigninApi(credential);
      await dispatch(fetchCurrentUser()).unwrap();
      toast.success(t('header.loginSuccess'), {
        autoClose: 3000,
        theme: 'colored',
      });
      setLoginOpen(false);
      setMenuOpen(false);
      setCredentials({ email: '', password: '' });
    } catch (_error) {
      toast.error(t('header.loginError'), {
        autoClose: 3000,
        theme: 'colored',
      });
    }
  };

  const renderGoogleLoginButton = (context: 'desktop' | 'mobile' = 'desktop') => (
    <div className={`menu-login__google menu-login__google--${context}`}>
      <div className="menu-login__separator"><span>{t('login.or')}</span></div>
      <GoogleLogin
        onSuccess={handleGoogleLoginSuccess}
        onError={() => {
          toast.error(t('header.loginError'), {
            autoClose: 3000,
            theme: 'colored',
          });
        }}
        text="continue_with"
        shape="rectangular"
        width="270"
      />
    </div>
  );

  const handleBellClick = () => {
    const nextOpen = !notificationOpen;
    setNotificationOpen(nextOpen);
    setChatMenuOpen(false);
    if (nextOpen && notificationCount > 0) {
      dispatch(clearBookingNotifications());
      dispatch(markAllNotificationsAsRead());
    }
  };

  const handleChatMenuClick = () => {
    const nextOpen = !chatMenuOpen;
    setChatMenuOpen(nextOpen);
    setNotificationOpen(false);
  };

  const openThreadFromHeader = useCallback(async (thread: ChatThreadItem, forceRouteMode = false) => {
    setChatMenuOpen(false);
    setChatDeleteCandidate(null);
    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }
    if (typingActiveRef.current && selectedChatThread?.interlocutorId) {
      typingActiveRef.current = false;
      sendCurrentUserTypingSignalApi({ toUserId: selectedChatThread.interlocutorId, isTyping: false }).catch(() => undefined);
    }
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 1060px)').matches;
    const shouldOpenRouteChat = forceRouteMode || isMobile || isMessagesPage;
    if (shouldOpenRouteChat) {
      setChatWindowOpen(false);
      if (thread.unreadCount > 0) {
        dispatch(markChatThreadAsReadLocal({ interlocutorId: thread.interlocutorId }));
        markCurrentUserConversationAsReadApi(thread.interlocutorId).catch(() => undefined);
      }
      navigate(`/messages?with=${encodeURIComponent(thread.interlocutorId)}`, {
        state: { interlocutorName: thread.interlocutorName },
        replace: isMessagesPage,
      });
      return;
    }
    setSelectedChatThread(thread);
    setChatWindowOpen(true);
    if (thread.unreadCount > 0) {
      dispatch(markChatThreadAsReadLocal({ interlocutorId: thread.interlocutorId }));
      markCurrentUserConversationAsReadApi(thread.interlocutorId).catch(() => undefined);
    }
  }, [dispatch, isMessagesPage, navigate, selectedChatThread?.interlocutorId]);

  const handleSelectChatThread = async (thread: ChatThreadItem) => {
    await openThreadFromHeader(thread);
  };

  useEffect(() => {
    const onOpenHeaderChat = (event: Event) => {
      const customEvent = event as CustomEvent<{ interlocutorId?: string; interlocutorName?: string }>;
      const interlocutorId = String(customEvent?.detail?.interlocutorId || '').trim();
      if (!interlocutorId) return;
      const existingThread = chatThreads.find((item) => item.interlocutorId === interlocutorId);
      const fallbackThread: ChatThreadItem = existingThread || {
        interlocutorId,
        interlocutorName: String(customEvent?.detail?.interlocutorName || '').trim() || 'Utilisateur',
        lastMessage: '',
        lastMessageAt: new Date().toISOString(),
        unreadCount: 0,
      };
      void openThreadFromHeader(fallbackThread, false);
    };

    window.addEventListener('open-header-chat', onOpenHeaderChat as EventListener);
    return () => window.removeEventListener('open-header-chat', onOpenHeaderChat as EventListener);
  }, [chatThreads, openThreadFromHeader]);

  const handleSendChatMessage = async () => {
    if (!selectedChatThread?.interlocutorId) return;
    if (chatSendingRef.current) return;
    const content = chatInput.trim();
    if (!content) return;
    pendingAutoScrollRef.current = true;
    chatSendingRef.current = true;
    const clientMessageId = chatClientMessageIdRef.current || createClientMessageId();
    chatClientMessageIdRef.current = clientMessageId;
    setChatSending(true);
    try {
      await sendCurrentUserMessageApi({
        toUserId: selectedChatThread.interlocutorId,
        content,
        clientMessageId,
      });
      setChatInput('');
      if (chatInputRef.current) {
        chatInputRef.current.style.height = `${CHAT_INPUT_MIN_HEIGHT}px`;
      }
      forceWindowChatToBottom('smooth');
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        if (typingStopTimerRef.current) {
          window.clearTimeout(typingStopTimerRef.current);
          typingStopTimerRef.current = null;
        }
        if (selectedChatThread?.interlocutorId) {
          sendCurrentUserTypingSignalApi({ toUserId: selectedChatThread.interlocutorId, isTyping: false }).catch(() => undefined);
        }
      }
    } catch (_error) {
      toast.error(t('header.chatSendError'));
    } finally {
      chatSendingRef.current = false;
      chatClientMessageIdRef.current = null;
      setChatSending(false);
    }
  };

  const handleChatInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    resizeChatInputElement(event.target);
    setChatInput(nextValue);
    const interlocutorId = selectedChatThread?.interlocutorId;
    if (!interlocutorId) return;

    const hasText = nextValue.trim().length > 0;
    if (hasText && !typingActiveRef.current) {
      typingActiveRef.current = true;
      sendCurrentUserTypingSignalApi({ toUserId: interlocutorId, isTyping: true }).catch(() => undefined);
    }

    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }

    if (!hasText) {
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        sendCurrentUserTypingSignalApi({ toUserId: interlocutorId, isTyping: false }).catch(() => undefined);
      }
      return;
    }

    typingStopTimerRef.current = window.setTimeout(() => {
      const activeInterlocutorId = selectedChatThread?.interlocutorId;
      if (!activeInterlocutorId || !typingActiveRef.current) return;
      typingActiveRef.current = false;
      sendCurrentUserTypingSignalApi({ toUserId: activeInterlocutorId, isTyping: false }).catch(() => undefined);
    }, 1400);
  };

  useLayoutEffect(() => {
    resizeChatInput();
  }, [chatInput, chatWindowOpen, resizeChatInput]);

  const handleConfirmDeleteChatMessage = async () => {
    if (!chatDeleteCandidate?.id) return;
    setChatDeleting(true);
    try {
      await deleteCurrentUserMessageApi(chatDeleteCandidate.id);
      setChatMessages((prev) => prev.filter((message) => message.id !== chatDeleteCandidate.id));
      setChatDeleteCandidate(null);
      toast.success(t('header.chatDeleteSuccess'));
    } catch (_error) {
      toast.error(t('header.chatDeleteError'));
    } finally {
      setChatDeleting(false);
    }
  };

  const truncateTitle = (value: string, maxLength = 28) => {
    const cleanValue = String(value || '').trim();
    if (!cleanValue) return '';
    if (cleanValue.length <= maxLength) return cleanValue;
    return `${cleanValue.slice(0, maxLength).trimEnd()}...`;
  };

  const truncateChatPreview = (value: string, maxLength = 46) => {
    const cleanValue = String(value || '').trim();
    if (!cleanValue) return t('header.chatNoMessage');
    if (cleanValue.length <= maxLength) return cleanValue;
    return `${cleanValue.slice(0, maxLength).trimEnd()}...`;
  };

  const formatNotificationDateTime = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const locale = (i18n.resolvedLanguage || 'fr').startsWith('ar')
      ? 'ar-DZ'
      : (i18n.resolvedLanguage || 'fr').startsWith('en')
        ? 'en-US'
        : 'fr-FR';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const capitalizeFirstLetter = (value: string) => value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;

  const capitalizeNotificationStart = (value: string) => {
    const trimmed = String(value || '').trimStart();
    if (!trimmed) return '';
    return `${trimmed.charAt(0).toLocaleUpperCase(i18n.resolvedLanguage || 'fr')}${trimmed.slice(1)}`;
  };

  const formatNotificationDateTimeLong = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const lang = (i18n.resolvedLanguage || 'fr').split('-')[0];
    if (lang === 'fr') {
      const locale = 'fr-FR';
      const weekday = capitalizeFirstLetter(new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date));
      const dayMonthYear = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${weekday} ${dayMonthYear} à ${hours}h${minutes}`;
    }
    const locale = lang === 'ar' ? 'ar-DZ' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  };

  const shouldShowEventDateTime = (notification: BookingValidatedNotificationItem) => (
    notification.type === 'booking_created_by_client' ||
    notification.type === 'booking_cancelled_by_client' ||
    notification.type === 'booking_cancelled' ||
    notification.type === 'booking_validated' ||
    notification.type === 'owner_reported'
  );

  const buildNotificationDateLine = (notification: BookingValidatedNotificationItem) => {
    if (!shouldShowEventDateTime(notification)) return '';
    const formatted = formatNotificationDateTimeLong(notification.createdAt);
    if (!formatted) return '';
    return t('header.notificationReservationDateLine', { dateTime: formatted });
  };

  const getReservationDatesFromNotification = (notification: BookingValidatedNotificationItem | null) => {
    if (!notification) return [] as string[];
    if (Array.isArray(notification.reservationDates) && notification.reservationDates.length > 0) {
      return Array.from(new Set(notification.reservationDates.filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value)))));
    }
    if (typeof notification.validatedDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(notification.validatedDate.trim())) {
      return [notification.validatedDate.trim()];
    }
    return [] as string[];
  };

  const buildNotificationShortMessage = (notification: BookingValidatedNotificationItem) => {
    const owner = notification.ownerName || t('header.notificationUnknownOwner');
    const client = notification.ownerName || t('header.notificationUnknownOwner');
    const adTitle = truncateTitle(notification.adTitle || t('header.notificationUnknownAd'));
    if (notification.type === 'owner_reported') {
      return t('header.notificationOwnerReportedShort', {
        owner,
        adTitle,
      });
    }
    if (notification.type === 'ad_pending_validation') {
      return t('header.notificationAdPendingShort', { adTitle });
    }
    if (notification.type === 'ad_validated') {
      return t('header.notificationAdValidatedShort', { adTitle });
    }
    if (notification.type === 'new_ad_created') {
      return t('header.notificationNewAdShort', { owner, adTitle });
    }
    if (notification.type === 'booking_created_by_client') {
      return t('header.notificationBookedByClientShort', { client, adTitle });
    }
    if (notification.type === 'booking_cancelled_by_client') {
      return t('header.notificationCancelledByClientShort', { client, adTitle });
    }
    if (notification.type === 'booking_cancelled_overlap') {
      return t('header.notificationCancelledOverlapShort', { adTitle });
    }
    return notification.type === 'booking_cancelled'
      ? t('header.notificationCancelledShort', { owner, adTitle })
      : t('header.notificationValidatedShort', { owner, adTitle });
  };

  const buildNotificationFullMessage = (notification: BookingValidatedNotificationItem) => {
    const owner = notification.ownerName || t('header.notificationUnknownOwner');
    const client = notification.ownerName || t('header.notificationUnknownOwner');
    const adTitle = notification.adTitle || t('header.notificationUnknownAd');
    if (notification.type === 'owner_reported') {
      const reportMessage = typeof notification.reportMessage === 'string' ? notification.reportMessage.trim() : '';
      const reporterName = typeof notification.reporterName === 'string' ? notification.reporterName.trim() : '';
      const reporterEmail = typeof notification.reporterEmail === 'string' ? notification.reporterEmail.trim() : '';
      const formattedReportedAt = formatNotificationDateTimeLong(notification.createdAt);
      const reportedAtLine = formattedReportedAt
        ? t('header.notificationOwnerReportedDateLine', { dateTime: formattedReportedAt })
        : '';
      const details = [
        t('header.notificationOwnerReportedFull', { owner, adTitle }),
        reporterName ? t('header.notificationOwnerReportedReporterName', { name: reporterName }) : '',
        reporterEmail ? t('header.notificationOwnerReportedReporterEmail', { email: reporterEmail }) : '',
        reportMessage ? t('header.notificationOwnerReportedMessage', { message: reportMessage }) : '',
        reportedAtLine,
      ].filter(Boolean);
      return details.join('\n');
    }
    const fallbackMessage = notification.type === 'ad_pending_validation'
      ? t('header.notificationAdPendingFull', { adTitle })
      : notification.type === 'ad_validated'
        ? t('header.notificationAdValidatedFull', { adTitle })
      : notification.type === 'new_ad_created'
        ? t('header.notificationNewAdFull', { owner, adTitle })
        : notification.type === 'booking_created_by_client'
          ? t('header.notificationBookedByClientFull', { client, adTitle, period: notification.validatedDate || '' })
        : notification.type === 'booking_cancelled_by_client'
          ? t('header.notificationCancelledByClientFull', { client, adTitle })
        : notification.type === 'booking_cancelled_overlap'
          ? t('header.notificationCancelledOverlapFull', { adTitle, period: notification.validatedDate || '' })
        : notification.type === 'booking_cancelled'
          ? t('header.notificationCancelledFull', { owner, adTitle })
          : t('header.notificationValidatedFull', { owner, adTitle });
    const notificationDateLine = buildNotificationDateLine(notification);
    const appendDateLine = (message: string) => (
      notificationDateLine ? `${message}\n${notificationDateLine}` : message
    );

    if (
      notification.type === 'booking_cancelled_overlap' ||
      notification.type === 'booking_created_by_client' ||
      notification.type === 'booking_cancelled_by_client' ||
      notification.type === 'booking_validated'
    ) {
      return appendDateLine(fallbackMessage);
    }
    if (!notification.validatedDate) return appendDateLine(fallbackMessage);
    const withValidatedDate = `${fallbackMessage} (${notification.validatedDate})`;
    return appendDateLine(withValidatedDate);
  };

  const handleNotificationChatClick = () => {
    if (!selectedNotification) return;
    const target = String(selectedNotification.chatTargetUserId || '').trim();
    setSelectedNotification(null);
    if (target) {
      navigate(`/messages?with=${encodeURIComponent(target)}`, {
        state: { interlocutorName: t('profileInfo.roleAdmin') },
      });
      return;
    }
    navigate('/messages');
  };

  const handleNotificationAdClick = () => {
    if (!selectedNotification?.adsId) return;
    const adId = String(selectedNotification.adsId).trim();
    setSelectedNotification(null);
    if (!adId) return;
    navigate(`/ads/${encodeURIComponent(adId)}`);
  };

  return (
    <>
      {shouldShowDevBanner && <div className="header__dev-banner">Environnement de test</div>}
      <header className={`header ${shouldShowDevBanner ? 'header--with-dev-banner' : ''}`} ref={headerRef}>
        <Link to="/" className="header__logo">
          <span className="header__logo-mark" aria-hidden="true">
            <svg
              className="header__logo-home"
              viewBox="0 0 24 24"
              focusable="false"
            >
              <path d="M4 10.5L12 4l8 6.5" />
              <path d="M6.5 9.8V19h11V9.8" />
              <path d="M10 19v-4.6a2 2 0 0 1 4 0V19" />
            </svg>
          </span>
          <span className="header__logo-text">
            <strong>{t('header.logoMain')}</strong>
            {t('header.logoSub') ? <em>{t('header.logoSub')}</em> : null}
          </span>
        </Link>

        <div className="header__actions">
          <div className={`menu-language ${languageOpen ? 'open' : ''}`} ref={languageDropdownRef}>
            <button
              className="menu-language__toggle"
              type="button"
              onClick={() => setLanguageOpen((prev) => !prev)}
              aria-label={t('header.language')}
              aria-haspopup="menu"
              aria-expanded={languageOpen}
            >
              <GlobeIcon className="menu-language__icon" aria-hidden="true" />
            </button>
            <ul className="menu-language__dropdown" role="menu">
              {languageItems.map((lang) => (
                <li key={lang.code}>
                  <button
                    type="button"
                    role="menuitem"
                    className={`menu-language__item ${currentLang === lang.code ? 'active' : ''}`}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLanguageOpen(false);
                    }}
                  >
                    <img className="menu-language__flag" src={lang.flagSrc} alt={lang.flagAlt} loading="lazy" />
                    <span>{lang.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {isAuthenticated && (
            <button
              ref={desktopChatRef}
              type="button"
              className={`header__chat-trigger ${chatUnreadCount > 0 ? 'has-unread' : ''} ${chatAnimating ? 'is-animating' : ''}`}
              aria-label={t('header.messagesAria', { count: chatUnreadCount })}
              onClick={handleChatMenuClick}
            >
              <MailIcon className="header__bell-icon" aria-hidden="true" />
              {chatUnreadCount > 0 && (
                <span className="header__bell-badge" aria-hidden="true">{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>
              )}
            </button>
          )}
          {isAuthenticated && (
            <button
              ref={desktopBellRef}
              type="button"
              className={`header__bell ${notificationCount > 0 ? 'has-notifications' : ''} ${bellAnimating ? 'is-animating' : ''}`}
              aria-label={t('header.notificationsAria', { count: notificationCount })}
              onClick={handleBellClick}
            >
              <BellIcon className="header__bell-icon" aria-hidden="true" />
              {notificationCount > 0 && (
                <span className="header__bell-badge" aria-hidden="true">{notificationCount > 99 ? '99+' : notificationCount}</span>
              )}
            </button>
          )}
          {user ? (
            <div className={`menu-account ${accountOpen ? 'open' : ''}`} ref={accountDropdownRef}>
              <button
                className="menu-account__toggle"
                type="button"
                onClick={() => setAccountOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
              >
                {t('header.account')}
              </button>

              <ul className="menu-account__dropdown">
                <li>
                  <Link to="/profile-info" onClick={() => setAccountOpen(false)}>
                    <UserIcon className="menu-account__item-icon" aria-hidden="true" />
                    <span>{t('header.profileInfo')}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/add-ads" onClick={handleAddAdLinkClick}>
                    <PlusCircleIcon className="menu-account__item-icon" aria-hidden="true" />
                    <span>{t('header.addAd')}</span>
                  </Link>
                </li>
                {canViewMyAds && (
                  <li>
                    <Link to="/my-ads" onClick={() => setAccountOpen(false)}>
                      <HomeIcon className="menu-account__item-icon" aria-hidden="true" />
                      <span>{t('header.myAds')}</span>
                    </Link>
                  </li>
                )}
                {canViewMyBookings && (
                  <li>
                    <Link to="/my-bookings" onClick={() => setAccountOpen(false)}>
                      <BookmarkIcon className="menu-account__item-icon" aria-hidden="true" />
                      <span>{t('header.myBookings')}</span>
                    </Link>
                  </li>
                )}
                {canViewMyReservations && (
                  <li>
                    <Link to="/reservation-management" onClick={() => setAccountOpen(false)}>
                      <ClipboardIcon className="menu-account__item-icon" aria-hidden="true" />
                      <span>{t('header.myReservations')}</span>
                    </Link>
                  </li>
                )}
                {canAccessDashboard && (
                  <li>
                    <Link to="/dashboard" onClick={() => setAccountOpen(false)}>
                      <GridIcon className="menu-account__item-icon" aria-hidden="true" />
                      <span>{t('header.dashboard')}</span>
                    </Link>
                  </li>
                )}
                <li className="menu-account__separator" />
                <li>
                  <button type="button" className="menu-account__logout" onClick={handleLogout}>
                    <LogoutIcon className="menu-account__item-icon" aria-hidden="true" />
                    <span>{t('header.logout')}</span>
                  </button>
                </li>
              </ul>
            </div>
          ) : !authResolved ? (
            <div className="header__auth-placeholder" aria-hidden="true" />
          ) : (
            <>
              <div className={`menu-login ${loginOpen ? 'open' : ''}`} ref={loginDropdownRef}>
                <button
                  className="header__ghost-btn header__ghost-btn--button header__login-btn"
                  type="button"
                  onClick={() => setLoginOpen((prev) => !prev)}
                  aria-haspopup="dialog"
                  aria-expanded={loginOpen}
                >
                  {t('header.login')}
                </button>
                <div className="menu-login__dropdown">
                  <form onSubmit={handleLoginSubmit} className="menu-login__form">
                    <label htmlFor="header-login-email">{t('header.emailOrUsername')}</label>
                    <input
                      id="header-login-email"
                      name="email"
                      type="text"
                      value={credentials.email}
                      onChange={handleLoginInputChange}
                      placeholder={t('header.emailPlaceholder')}
                      required
                    />

                    <label htmlFor="header-login-password">{t('header.password')}</label>
                    <div className="menu-login__password-wrap">
                      <input
                        id="header-login-password"
                        name="password"
                        type={showLoginPassword ? 'text' : 'password'}
                        value={credentials.password}
                        onChange={handleLoginInputChange}
                        placeholder={t('header.passwordPlaceholder')}
                        required
                      />
                      <button
                        type="button"
                        className="menu-login__password-toggle"
                        onClick={() => setShowLoginPassword((prev) => !prev)}
                        aria-label={showLoginPassword ? t('header.hidePassword') : t('header.showPassword')}
                        title={showLoginPassword ? t('header.hidePassword') : t('header.showPassword')}
                      >
                        {showLoginPassword ? <EyeOffIcon aria-hidden={true} /> : <EyeIcon aria-hidden={true} />}
                      </button>
                    </div>

                    <div className="menu-login__primary-actions">
                      <button type="submit" disabled={loading}>
                        {loading ? t('header.loginLoading') : t('header.loginSubmit')}
                      </button>
                      <Link
                        to="/forgot-password"
                        className="menu-login__forgot-btn"
                        onClick={() => setLoginOpen(false)}
                      >
                        {t('header.forgotPassword')}
                      </Link>
                      <Link
                        to="/register"
                        className="menu-login__signup-btn"
                        onClick={() => setLoginOpen(false)}
                      >
                        {t('header.signup')}
                      </Link>
                    </div>
                    {renderGoogleLoginButton()}
                  </form>
                </div>
              </div>
            </>
          )}
        </div>

        {isAuthenticated && (
          <button
            ref={mobileChatRef}
            type="button"
            className={`header__mobile-chat ${chatUnreadCount > 0 ? 'has-unread' : ''} ${chatAnimating ? 'is-animating' : ''}`}
            aria-label={t('header.messagesAria', { count: chatUnreadCount })}
            onClick={handleChatMenuClick}
          >
            <MailIcon className="header__bell-icon" aria-hidden="true" />
            {chatUnreadCount > 0 && (
              <span className="header__bell-badge" aria-hidden="true">{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>
            )}
          </button>
        )}
        {isAuthenticated && (
          <button
            ref={mobileBellRef}
            type="button"
            className={`header__mobile-bell ${notificationCount > 0 ? 'has-notifications' : ''} ${bellAnimating ? 'is-animating' : ''}`}
            aria-label={t('header.notificationsAria', { count: notificationCount })}
            onClick={handleBellClick}
          >
            <BellIcon className="header__bell-icon" aria-hidden="true" />
            {notificationCount > 0 && (
              <span className="header__bell-badge" aria-hidden="true">{notificationCount > 99 ? '99+' : notificationCount}</span>
            )}
          </button>
        )}
        <button className="header__burger" onClick={toggleMenu} aria-label={t('header.openMenu')}>
          <MenuIcon aria-hidden="true" />
        </button>

        <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
          <button className="mobile-menu__close" onClick={toggleMenu} aria-label={t('header.closeMenu')}>
            <CloseIcon aria-hidden="true" />
          </button>
          <div className="mobile-menu__actions">
            <div className={`mobile-language ${mobileLanguageOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="mobile-language__toggle"
                onClick={() => setMobileLanguageOpen((prev) => !prev)}
                aria-label={t('header.language')}
                aria-haspopup="menu"
                aria-expanded={mobileLanguageOpen}
              >
                <GlobeIcon aria-hidden="true" />
              </button>
              <ul className="mobile-language__dropdown" role="menu">
                {languageItems.map((lang) => (
                  <li key={lang.code}>
                    <button
                      type="button"
                      role="menuitem"
                      className={`mobile-language__item ${currentLang === lang.code ? 'active' : ''}`}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setMobileLanguageOpen(false);
                      }}
                    >
                      <img className="mobile-language__flag" src={lang.flagSrc} alt={lang.flagAlt} loading="lazy" />
                      <span>{lang.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {user ? (
              <>
                <div className="mobile-menu__nav">
                  <Link to="/profile-info" className="mobile-menu__item" onClick={toggleMenu}>
                    <UserIcon className="mobile-menu__item-icon" aria-hidden="true" />
                    <span>{t('header.profileInfo')}</span>
                  </Link>
                  <Link
                    to="/add-ads"
                    className="mobile-menu__item"
                    onClick={(event) => handleAddAdLinkClick(event, { mobile: true })}
                  >
                    <PlusCircleIcon className="mobile-menu__item-icon" aria-hidden="true" />
                    <span>{t('header.addAd')}</span>
                  </Link>
                  {canViewMyAds && (
                    <Link to="/my-ads" className="mobile-menu__item" onClick={toggleMenu}>
                      <HomeIcon className="mobile-menu__item-icon" aria-hidden="true" />
                      <span>{t('header.myAds')}</span>
                    </Link>
                  )}
                  {canViewMyBookings && (
                    <Link to="/my-bookings" className="mobile-menu__item" onClick={toggleMenu}>
                      <BookmarkIcon className="mobile-menu__item-icon" aria-hidden="true" />
                      <span>{t('header.myBookings')}</span>
                    </Link>
                  )}
                  {canViewMyReservations && (
                    <Link to="/reservation-management" className="mobile-menu__item" onClick={toggleMenu}>
                      <ClipboardIcon className="mobile-menu__item-icon" aria-hidden="true" />
                      <span>{t('header.myReservations')}</span>
                    </Link>
                  )}
                  {canAccessDashboard && (
                    <Link to="/dashboard" className="mobile-menu__item" onClick={toggleMenu}>
                      <GridIcon className="mobile-menu__item-icon" aria-hidden="true" />
                      <span>{t('header.dashboard')}</span>
                    </Link>
                  )}
                  <div className="mobile-menu__divider" aria-hidden="true" />
                  <button type="button" className="mobile-menu__item mobile-menu__item--logout" onClick={handleLogout}>
                    <LogoutIcon className="mobile-menu__item-icon" aria-hidden="true" />
                    <span>{t('header.logout')}</span>
                  </button>
                </div>
              </>
            ) : !authResolved ? (
              <div className="mobile-auth-placeholder" aria-hidden="true" />
            ) : (
              <>
                <form onSubmit={handleLoginSubmit} className="mobile-login-form">
                  <label htmlFor="mobile-login-email">{t('header.emailOrUsername')}</label>
                  <input
                    id="mobile-login-email"
                    name="email"
                    type="text"
                    value={credentials.email}
                    onChange={handleLoginInputChange}
                    placeholder={t('header.emailPlaceholder')}
                    required
                  />

                  <label htmlFor="mobile-login-password">{t('header.password')}</label>
                  <div className="mobile-login-form__password-wrap">
                    <input
                      id="mobile-login-password"
                      name="password"
                      type={showLoginPassword ? 'text' : 'password'}
                      value={credentials.password}
                      onChange={handleLoginInputChange}
                      placeholder={t('header.passwordPlaceholder')}
                      required
                    />
                    <button
                      type="button"
                      className="mobile-login-form__password-toggle"
                      onClick={() => setShowLoginPassword((prev) => !prev)}
                      aria-label={showLoginPassword ? t('header.hidePassword') : t('header.showPassword')}
                      title={showLoginPassword ? t('header.hidePassword') : t('header.showPassword')}
                    >
                      {showLoginPassword ? <EyeOffIcon aria-hidden={true} /> : <EyeIcon aria-hidden={true} />}
                    </button>
                  </div>

                  <button type="submit" disabled={loading} className="mobile-login-form__submit">
                    {loading ? t('header.loginLoading') : t('header.loginSubmit')}
                  </button>
                  <Link to="/forgot-password" className="mobile-login-form__forgot" onClick={toggleMenu}>
                    {t('header.forgotPassword')}
                  </Link>
                  <Link to="/register" className="mobile-login-form__signup" onClick={toggleMenu}>
                    {t('header.signup')}
                  </Link>
                  {renderGoogleLoginButton('mobile')}
                </form>
              </>
            )}
          </div>
        </div>

        {user && (
          <div
            ref={notificationsPanelRef}
            className={`header__notifications-panel ${notificationOpen ? 'open' : ''}`}
            style={notificationPanelStyle}
            role="menu"
            aria-label={t('header.notificationsAria', { count: notificationCount })}
          >
            <div className="header__notifications-title">{t('header.notificationsTitle')}</div>
            <div className="header__notifications-list">
              {notifications.length === 0 ? (
                <div className="header__notifications-empty">{t('header.notificationsEmpty')}</div>
              ) : (
                notifications.map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    className="header__notification-item"
                    onClick={() => setSelectedNotification(notification)}
                    title={buildNotificationFullMessage(notification)}
                  >
                    <span className="header__notification-item-text">{capitalizeNotificationStart(buildNotificationShortMessage(notification))}</span>
                    <span className="header__notification-item-time">{formatNotificationDateTime(notification.createdAt)}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {user && (
          <div
            ref={chatsPanelRef}
            className={`header__chats-panel ${chatMenuOpen ? 'open' : ''}`}
            style={chatPanelStyle}
            role="menu"
            aria-label={t('header.messagesAria', { count: chatUnreadCount })}
          >
            <div className="header__notifications-title">{t('header.messagesTitle')}</div>
            <div className="header__notifications-list header__chats-list">
              {chatThreads.length === 0 ? (
                <div className="header__notifications-empty">{t('header.messagesEmpty')}</div>
              ) : (
                chatThreads.map((thread) => (
                  <button
                    key={thread.interlocutorId}
                    type="button"
                    className={`header__chat-item ${thread.unreadCount > 0 ? 'header__chat-item--unread' : ''}`}
                    onClick={() => handleSelectChatThread(thread)}
                  >
                    <span className="header__chat-item-title">{thread.interlocutorName}</span>
                    <span className="header__chat-item-text">{truncateChatPreview(thread.lastMessage)}</span>
                  </button>
                ))
              )}
            </div>
            {chatThreads.length > 0 && (
              <div className="header__chats-panel-footer">
                <button
                  type="button"
                  className="header__chats-panel-link"
                  onClick={() => {
                    setChatMenuOpen(false);
                    navigate('/messages');
                  }}
                >
                  {t('header.chatOpenFullPage')}
                </button>
              </div>
            )}
          </div>
        )}

        {selectedNotification && (
          <div
            className="header__notification-modal-overlay"
            onClick={() => setSelectedNotification(null)}
          >
            <div
              className="header__notification-modal"
              role="dialog"
              aria-modal="true"
              aria-label={t('header.notificationsDetailTitle')}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="header__notification-modal-title">{t('header.notificationsDetailTitle')}</div>
              <p className="header__notification-modal-text">{capitalizeNotificationStart(buildNotificationFullMessage(selectedNotification))}</p>
              {(selectedNotification.type === 'booking_validated' || selectedNotification.type === 'booking_cancelled_by_client')
                && getReservationDatesFromNotification(selectedNotification).length > 0 && (
                <>
                  <button
                    type="button"
                    className="header__notification-modal-secondary"
                    onClick={() => setShowReservationDetails((prev) => !prev)}
                  >
                    {showReservationDetails
                      ? t('header.notificationHideReservationDetails')
                      : t('header.notificationShowReservationDetails')}
                  </button>
                  {showReservationDetails && (
                    <div className="header__notification-reservation-details">
                      <p className="header__notification-reservation-details-text">
                        {t('header.notificationReservationDetailsIntro')}
                      </p>
                      <div className="header__notification-reservation-tags">
                        {getReservationDatesFromNotification(selectedNotification).map((dateKey) => (
                          <span key={dateKey} className="header__notification-reservation-tag">
                            {dateKey}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="header__notification-modal-actions">
                {selectedNotification.type === 'ad_validated' && selectedNotification.adsId && (
                  <button
                    type="button"
                    className="header__notification-modal-secondary"
                    onClick={handleNotificationAdClick}
                  >
                    {t('header.notificationViewAd')}
                  </button>
                )}
                {selectedNotification.type === 'ad_pending_validation' && (
                  <button
                    type="button"
                    className="header__notification-modal-primary"
                    onClick={handleNotificationChatClick}
                  >
                    {t('header.notificationContactAdmin')}
                  </button>
                )}
                <button
                  type="button"
                  className="header__notification-modal-close"
                  onClick={() => setSelectedNotification(null)}
                >
                  {t('header.notificationsClose')}
                </button>
              </div>
            </div>
          </div>
        )}

        {missingPhoneModalOpen && (
          <div
            className="header__notification-modal-overlay"
            onClick={() => setMissingPhoneModalOpen(false)}
          >
            <div
              className="header__notification-modal"
              role="dialog"
              aria-modal="true"
              aria-label={t('header.missingPhoneTitle')}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="header__notification-modal-title">{t('header.missingPhoneTitle')}</div>
              <p className="header__notification-modal-text">{t('header.missingPhoneBody')}</p>
              <div className="header__notification-modal-actions">
                <Link
                  to={userProfileEditPath}
                  className="header__notification-modal-primary header__notification-modal-link"
                  onClick={() => setMissingPhoneModalOpen(false)}
                >
                  {t('header.missingPhoneProfileLink')}
                </Link>
                <button
                  type="button"
                  className="header__notification-modal-close"
                  onClick={() => setMissingPhoneModalOpen(false)}
                >
                  {t('header.notificationsClose')}
                </button>
              </div>
            </div>
          </div>
        )}

        {accountConfirmationOpen && (
          <div
            className="header__notification-modal-overlay"
            onClick={() => setAccountConfirmationOpen(false)}
          >
            <div
              className="header__notification-modal"
              role="dialog"
              aria-modal="true"
              aria-label={t('header.confirmAccountTitle')}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="header__notification-modal-title">{t('header.confirmAccountTitle')}</div>
              <p className="header__notification-modal-text">{t('header.confirmAccountBody1')}</p>
              <p className="header__notification-modal-text">{t('header.confirmAccountBody2')}</p>
              <form className="header__confirm-account-form" onSubmit={handleResendConfirmationEmail}>
                <input
                  type="email"
                  className="header__confirm-account-input"
                  value={resendConfirmationEmail}
                  onChange={(event) => setResendConfirmationEmail(event.target.value)}
                  placeholder={t('header.emailPlaceholder')}
                />
                <div className="header__notification-modal-actions">
                  <button
                    type="submit"
                    className="header__notification-modal-primary"
                    disabled={resendingConfirmation}
                  >
                    {resendingConfirmation ? t('header.confirmAccountSending') : t('header.confirmAccountSubmit')}
                  </button>
                  <button
                    type="button"
                    className="header__notification-modal-close"
                    onClick={() => setAccountConfirmationOpen(false)}
                    disabled={resendingConfirmation}
                  >
                    {t('header.notificationsClose')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {chatWindowOpen && selectedChatThread && (
          <div className="header__chat-window" role="dialog" aria-modal="false" aria-label={t('header.chatWindowTitle', { name: selectedChatThread.interlocutorName })}>
            <div className="header__chat-window-head">
              <div className="header__chat-window-title">{selectedInterlocutorName}</div>
                <button
                  type="button"
                  className="header__chat-window-close"
                onClick={() => {
                  setChatWindowOpen(false);
                  setChatDeleteCandidate(null);
                  if (typingStopTimerRef.current) {
                    window.clearTimeout(typingStopTimerRef.current);
                    typingStopTimerRef.current = null;
                  }
                  if (typingActiveRef.current && selectedChatThread?.interlocutorId) {
                    typingActiveRef.current = false;
                    sendCurrentUserTypingSignalApi({ toUserId: selectedChatThread.interlocutorId, isTyping: false }).catch(() => undefined);
                  }
                }}
                aria-label={t('header.notificationsClose')}
                >
                  <CloseIcon aria-hidden="true" />
                </button>
            </div>
            <div
              ref={chatMessagesRef}
              className="header__chat-window-body"
              onScroll={handleWindowChatBodyScroll}
              onWheel={handleWindowChatBodyWheel}
              onTouchStart={handleWindowChatBodyTouchStart}
              onTouchMove={handleWindowChatBodyTouchMove}
            >
              {chatLoading ? (
                <div className="header__chat-window-empty">{t('header.chatLoading')}</div>
              ) : chatMessages.length === 0 ? (
                <div className="header__chat-window-empty">{t('header.chatNoMessage')}</div>
              ) : (
                <>
                {chatLoadingOlder && (
                  <div className="header__chat-history-loader">{t('header.chatLoading')}</div>
                )}
                {chatWindowItems.map((item) => {
                  if (item.type === 'date') {
                    return (
                      <div key={item.id} className="header__chat-date-separator">
                        <span>{item.label}</span>
                      </div>
                    );
                  }
                  const message = item.message;
                  const senderName = message.isMine ? myDisplayName : selectedInterlocutorName;
                  const sentAt = formatChatMessageTime(message.createdAt);
                  const showMessageSeen = Boolean(
                    message.isMine &&
                    lastChatMessage?.id === message.id &&
                    message.readAt
                  );
                  return (
                  <div
                    key={message.id}
                    className={`header__chat-message-row ${message.isMine ? 'mine' : 'theirs'}`}
                  >
                    <div className={`header__chat-message-top ${message.isMine ? 'mine' : 'theirs'}`}>
                      {!message.isMine && (
                        <img src={interlocutorAvatar} alt={selectedInterlocutorName} className="header__chat-avatar" loading="lazy" />
                      )}
                    {message.isMine && (
                      <button
                        type="button"
                        className="header__chat-bubble-more"
                        onClick={() => setChatDeleteCandidate({ id: message.id, content: message.content })}
                        aria-label={t('header.chatDeleteMenuAria')}
                      >
                        <span aria-hidden="true">⋮</span>
                      </button>
                    )}
                      <div className={`header__chat-message-meta ${message.isMine ? 'mine' : 'theirs'}`}>
                        {message.isMine ? (
                          <>
                            {sentAt ? <span>{sentAt}</span> : null}
                            <strong>{senderName}</strong>
                          </>
                        ) : (
                          <>
                            <strong>{senderName}</strong>
                            {sentAt ? <span>{sentAt}</span> : null}
                          </>
                        )}
                      </div>
                      {message.isMine && (
                        <img src={myAvatar} alt={myDisplayName} className="header__chat-avatar" loading="lazy" />
                      )}
                    </div>
                    <div className="header__chat-message-content">
                      <div className={`header__chat-bubble ${message.isMine ? 'mine' : 'theirs'}`}>
                        <ChatMessageContent content={message.content} />
                      </div>
                      {showMessageSeen && (
                        <div className="header__chat-seen">{t('header.chatSeen')}</div>
                      )}
                    </div>
                  </div>
                )})}
                </>
              )}
              {isSelectedInterlocutorTyping && (
                <div className="header__chat-message-row theirs">
                  <img src={interlocutorAvatar} alt={selectedInterlocutorName} className="header__chat-avatar" loading="lazy" />
                  <div className="header__chat-typing" aria-live="polite" aria-label={t('header.chatTyping')}>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}
            </div>
            <div className="header__chat-window-foot">
              <textarea
                ref={chatInputRef}
                value={chatInput}
                onChange={handleChatInputChange}
                className="header__chat-input"
                rows={1}
                placeholder={t('header.chatPlaceholder')}
              />
              <button
                type="button"
                className="header__chat-send"
                onClick={handleSendChatMessage}
                disabled={chatSending || !chatInput.trim()}
                aria-label={t('header.chatSend')}
                title={t('header.chatSend')}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M3 20L21 12L3 4L3.01 10L16 12L3.01 14L3 20Z" fill="currentColor" />
                </svg>
              </button>
            </div>
            {chatDeleteCandidate && (
              <div className="header__chat-delete-modal-overlay" onClick={() => setChatDeleteCandidate(null)}>
                <div className="header__chat-delete-modal" onClick={(event) => event.stopPropagation()}>
                  <p className="header__chat-delete-modal-title">{t('header.chatDeleteConfirmTitle')}</p>
                  <p className="header__chat-delete-modal-text">{t('header.chatDeleteConfirmText')}</p>
                  <div className="header__chat-delete-modal-actions">
                    <button
                      type="button"
                      className="header__chat-delete-cancel"
                      onClick={() => setChatDeleteCandidate(null)}
                      disabled={chatDeleting}
                    >
                      {t('header.chatDeleteCancel')}
                    </button>
                    <button
                      type="button"
                      className="header__chat-delete-confirm"
                      onClick={handleConfirmDeleteChatMessage}
                      disabled={chatDeleting}
                    >
                      {chatDeleting ? t('header.chatDeleteLoading') : t('header.chatDeleteConfirm')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {menuOpen && <div className="overlay" onClick={toggleMenu} />}
      </header>
    </>
  );
};

export default Header;
